/**
 * `ScopeBroadcast` — the console's tabs telling each other the scope is about to move, or moved
 * (plan-014 A4, ED-12, design §27.10.18 as amended: the seam is named `ScopeBroadcast`).
 *
 * One `BroadcastChannel('wetalk-scope')`. A message carries ONLY `{ kind, generation }`: no
 * organization, workspace, person or page ever crosses it, so nothing about one organization can
 * leak into a tab that shows another.
 *
 *   scope_change_intent   a tab is about to switch; tabs with unsaved edits answer `dirty`
 *   dirty                 "I have unsaved edits" — counted by the asking tab for
 *                         {@link SCOPE_DIRTY_WINDOW_MS}, then the switch asks its person
 *   scope_changed         a tab switched; the others go stale and close the previous scope
 *
 * The broadcast is the FAST path, never the guarantee: a tab that misses it (no BroadcastChannel in
 * that browser, a frozen background tab, another browser window) is still refused `409 scope_changed`
 * by the server on its next scoped request (`./scoped-fetch.ts`). That is why a browser without
 * `BroadcastChannel` gets a broadcast that tells no other tab: the server check still holds, and a
 * switch is never blocked on a channel that cannot exist.
 */

export const SCOPE_BROADCAST_NAME = "wetalk-scope";

/**
 * How long a switching tab waits for other tabs to say they hold unsaved edits. Tabs answer from
 * memory (no I/O), so a quarter of a second is generous on a busy machine and short enough not to be
 * felt as a pause before the switch.
 */
export const SCOPE_DIRTY_WINDOW_MS = 250;

export const SCOPE_MESSAGE_KIND = ["scope_change_intent", "dirty", "scope_changed"] as const;
export type ScopeMessageKind = (typeof SCOPE_MESSAGE_KIND)[number];

/** The whole message. Nothing else is ever posted. */
export interface ScopeMessage {
  readonly kind: ScopeMessageKind;
  readonly generation: number;
}

/** A message as received: anything that is not exactly `{ kind, generation }` is ignored. */
export function scopeMessageOf(data: unknown): ScopeMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as Record<string, unknown>;
  const kind = record["kind"];
  const generation = record["generation"];
  if (typeof kind !== "string" || !(SCOPE_MESSAGE_KIND as readonly string[]).includes(kind)) return null;
  if (typeof generation !== "number" || !Number.isSafeInteger(generation) || generation < 1) return null;
  if (Object.keys(record).length !== 2) return null;
  return { kind: kind as ScopeMessageKind, generation };
}

/** The slice of `BroadcastChannel` the broadcast uses, so a suite can hand it a pair of fakes. */
export interface ScopePort {
  postMessage(message: ScopeMessage): void;
  addEventListener(type: "message", listener: (event: { readonly data: unknown }) => void): void;
  removeEventListener(type: "message", listener: (event: { readonly data: unknown }) => void): void;
  close(): void;
}

export interface ScopeBroadcast {
  /**
   * Asks the other tabs whether any holds unsaved edits before this tab switches. `next.kind` says
   * what is about to change; it is for the caller's own wording and is never posted.
   */
  announceIntent(next: { readonly kind: "account" | "workspace" }): Promise<{ readonly dirtyTab: number }>;
  /** This tab switched and now holds `generation`. */
  announceChanged(generation: number): void;
  /** Another tab switched to `generation`. Returns the unsubscribe. */
  onChanged(listener: (generation: number) => void): () => void;
  close(): void;
}

export interface ScopeBroadcastDependencies {
  /** The channel, or `null` where the browser has none (see the module note). */
  readonly port: ScopePort | null;
  /** The generation this tab is on, posted with an intent; `null` before any. */
  readonly generation: () => number | null;
  /** Whether this tab holds unsaved edits right now (`useScopeGuard().markDirty`). */
  readonly isDirty: () => boolean;
  readonly setTimer: (run: () => void, ms: number) => () => void;
  readonly dirtyWindowMs: number;
}

/** The browser's channel, or `null` where `BroadcastChannel` does not exist. */
export function browserScopePort(): ScopePort | null {
  if (typeof globalThis.BroadcastChannel !== "function") return null;
  return new globalThis.BroadcastChannel(SCOPE_BROADCAST_NAME) as unknown as ScopePort;
}

export function createScopeBroadcast(dependencies: ScopeBroadcastDependencies): ScopeBroadcast {
  const { port } = dependencies;
  const changed = new Set<(generation: number) => void>();
  /** Open intents this tab is counting answers for, by the generation they were posted under. */
  const counting = new Map<number, { count: number }>();

  const onMessage = (event: { readonly data: unknown }): void => {
    const message = scopeMessageOf(event.data);
    if (message === null || port === null) return;
    if (message.kind === "scope_change_intent") {
      if (dependencies.isDirty()) port.postMessage({ kind: "dirty", generation: message.generation });
      return;
    }
    if (message.kind === "dirty") {
      const open = counting.get(message.generation);
      if (open !== undefined) open.count += 1;
      return;
    }
    for (const one of [...changed]) one(message.generation);
  };
  port?.addEventListener("message", onMessage);

  return {
    announceIntent() {
      const generation = dependencies.generation();
      if (port === null || generation === null) return Promise.resolve({ dirtyTab: 0 });
      // One count per generation: a second intent while one is open shares it.
      const open = counting.get(generation) ?? { count: 0 };
      counting.set(generation, open);
      port.postMessage({ kind: "scope_change_intent", generation });
      return new Promise((resolve) => {
        dependencies.setTimer(() => {
          counting.delete(generation);
          resolve({ dirtyTab: open.count });
        }, dependencies.dirtyWindowMs);
      });
    },
    announceChanged(generation) {
      port?.postMessage({ kind: "scope_changed", generation });
    },
    onChanged(listener) {
      changed.add(listener);
      return () => {
        changed.delete(listener);
      };
    },
    close() {
      port?.removeEventListener("message", onMessage);
      port?.close();
      changed.clear();
      counting.clear();
    },
  };
}
